// Shared types

import { PageSlugs } from '../add-element/types';
import { Descendant } from 'slate';

export type TimestampedModel = {
  id: number
  createdAt: string,
  updatedAt: string,
  // In order to do Object.keys and Object.values, we have to
  // extend object explicitly for all of our models
  // eslint-disable-next-line @typescript-eslint/ban-types
} & object;

export type SortableFields = {
  position: number,
}

export type HasImageAndPhoto = {
  id: number,
  // represents cropped image on all models except Attendants
  image?: string | null,
  // represents foreign key to photo table, is a string on Attendants and
  // represents the cropped image on that model
  photo: number | null,
}

export type GeneralContentFields = {
  title: string | null,
  content: RichTextType,
} & HasImageAndPhoto;

export type SectionMedia = {
  resourceName: string,
  resource: {
    data: (Photo | Video) & { position: number },
  },
} & (Photo | Video) & GalleryMedia;

export type AddressFields = {
  venue: string | null;
  hasPhysicalAddress: boolean;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
}

// This represents all editable resources, so sections, accommodations,
// attendants, etc. That's why many fields are optional
export type EditableResourceFields = {
  // Title is the primary freetext field for most resources, but it's
  // the formal title of the Attendant and the Name is primary
  title: string,
  // Accommodations
  description?: RichTextType | null,
  url?: string,
  // Attendants
  bio?: RichTextType,
  name?: string,
  // This is only on here for attendants, which uses 'photo' where it should use
  // 'image' so we had to name this photoPk
  photoPk?: number | null,

  // Event fields, used for display only, the event form is its own component
  date?: string,
  endDate?: string,
  time?: string,
  endTime?: string,

  // Gallery sections
  sectionMedia?: SectionMedia[]
  sectionType?: string,
  galleryMedia?: GalleryMedia[],
  galleryMode?: string,

  // Custom sections
  content?: RichTextType;
  // Exists on the resource models
  position: number,
} & HasImageAndPhoto & Partial<AddressFields>;

export type Configuration = {
  imageAlignment: string,
  imageSize: number,
  pageLayout: string,
  showMap: boolean,
  showPageDividers?: boolean,
  textAlignment: string,
  rsvpSubheading?: string,
}

export type RichTextType = Descendant[];

// Demo types

export type DemoBasis = {
  id: string, // UUID field, override of TimestampedModel
  data: Record<string, string>,
  title: string,
  slug: string,
  kinds: string[],
  schema: Record<string, string>,
  siteSchema: Record<string, string>,
} & TimestampedModel;

export type DemoSite = {
  basis: string, // DemoBasis ID
  product: string, // Product ID
  data: Record<string, string>,
} & TimestampedModel;

// Site types

export type SeenUpsell = {
  code: string,
  date: string,
}

export enum TestValues {
  Control = 'A',
  Experiment = 'B',
  Variant = 'C',
  Disabled = 'OFF',
}

export type SiteDetails = {
  // Announcement (in settings page)
  showAnnouncement: boolean,
  announcementBody: string,
  announcementHeading: string,
  // Wedding date visibility (also in settings page)
  isWeddingDateVisible: boolean,
  // Your Names fields
  yourName: string,
  nameSeparator: string,
  spouseName: string,
  // The Big Day fields
  eventDateHeading: string,
  weddingDate: string,
  isCountdownVisible: boolean,
  countdownText: string,
  // Welcome Message fields
  isOurStoryVisible: boolean,
  welcomeHeading: string,
  welcomeText: string,
  // Not directly user controlled
  seenUpsells: SeenUpsell[],
  isUsingGuestManagement: boolean,
  isUsingNewComponents: boolean,
  sectionEditorExperience: TestValues,
  globalStylingChanges: TestValues,
}

export type Site = {
  announcementBody: string | null,
  announcementHeading: string | null,
  customColorTheme: number | null,
  status: string,
  firstEditedAt: string,
  domain: string,
  domainExpiration: string,
  domainHasExpired: boolean,
  domainIsCustom: boolean,
  domainIsExpiring: boolean,
  editingExpiration: string,
  isDemoSite?: boolean,
  isWeddingDateVisible: boolean | null,
  source: string,
  hasCustomDomainFeature: boolean,
  assets: Record<string, string>,
  themeIdentifier: 'l1' | 'l2' | 'l3' | 'l4',
  themeId: number,
  mintedProductSku: string,
  mintedProductColorChoiceId: string,
  themeDataOverrides: Record<string, string>,
  pageConfiguration: string,
  showAnnouncement: boolean,
  siteType: string,
  siteDetails: SiteDetails,
  accessKey: string,
  isHoliday: boolean,
  isOnlineInvitation: boolean,
  isWedding: boolean,
  hasRsvpEvents: boolean,
  eventHeading: string,
  searchEngineVisibility: boolean,
  mintedSearchVisibility: boolean,
} & TimestampedModel;

// Pages and general section types

export type Page = {
  site: number, // Page ID

  isDefault: boolean,
  isVisible: boolean,
  defaultName: string,
  displayName: string,
  givenName: string,
  slug: PageSlugs,
  configuration: Configuration,
  sections: Section[],
} & TimestampedModel & SortableFields & GeneralContentFields;

export type Photo = {
  original: string,
  preview: string,
  versions: Record<string, string>,
  id: string, // same as UUID, backend does mapping
  uuid: string,
  caption: string,
  pk: number, // Photo.id represents its UUID, but pk is the actual id
} & TimestampedModel;

export type Video = {
  id: string,
  pk: string,
  publicId: string,
  caption: string,
  duration: string,
  preview: string, // thumbnail
} & TimestampedModel;

export const MediaTypes = {
  photo: 'photo',
  video: 'video',
};

// Added by serializer on the output, combining
export type GalleryMedia = {
  caption: string | null,
  createdAt: string,
  duration?: string, // video specific attribute
  id: string | number,
  mediaType: 'photo' | 'video',
  original?: string,
  pk: string,
  position: number,
  preview: string,
  publicId?: string, // video specific attribute
  updatedAt?: string,
  image?: string | null, // represents a cropped photo's url
}

// Generalized section and section details models

export enum SectionTypes {
  // These section types are "primary" types for specific pages
  Registry = 'registry',
  Accommodation = 'accommodation',
  Attendant = 'attendant',
  // Event is not actually a section type, but we mix the event models
  // so that the events page can use the new system
  Event__FAKE = 'event',
  // These are shared across all page types
  Text = 'text',
  ImageAndText = 'image_and_text',
  Gallery = 'gallery',
  Media = 'media',
  Divider = 'divider',
  Button = 'button',
  Anchor = 'anchor',
  CashRegistry = 'cash_registry',
  Location = 'location',
}

export type TextDetails = {
  title: string | null;
  content: RichTextType | null;
}

export type ImageAndTextDetails = {
  displayMode: string, // Replace with enum
} & TextDetails;

export type GalleryDetails = {
  title: string | null;
  content: RichTextType | null;
  galleryMode: string; // Replace with enum
}

export type MediaDetails = {
  // Currently empty
}

export type DividerDetails = {
  dividerType: string; // Replace with enum
}

export type ButtonDetails = {
  websiteUrl: string;
  buttonText: string | null;
}

export type AnchorDetails = {
  websiteUrl: string;
  anchorText: string | null;
}

export type AccommodationDetails = {
  title: string;
  content: RichTextType | null;
  websiteUrl: string | null;
  anchorText: string | null;
}

export type RegistryDetails = {
  title: string;
  content: RichTextType | null;
  websiteUrl: string | null;
  buttonText: string | null;
}

export type CashRegistryDetails = RegistryDetails & {
  // Embedded experience doesn't allow custom button text
  buttonText: null,
  cashRegistryAccessToken: string;
  cashRegistryId: string;
}

export type AttendantDetails = {
  title: string;
  content: RichTextType | null;
  name: string;
}

export type LocationDetails = {
  title: string;
  content: RichTextType | null;
} & AddressFields;

export type SectionDetailsType = TextDetails
  | ImageAndTextDetails
  | GalleryDetails
  | MediaDetails
  | DividerDetails
  | ButtonDetails
  | AnchorDetails
  | AccommodationDetails
  | RegistryDetails
  | CashRegistryDetails
  | AttendantDetails
  | LocationDetails;

export type Section = {
  id: number,
  page: number, // Page ID
  // legacy fields title and content should not be used in new system
  title: string,
  content: RichTextType | null,
  sectionDetails: SectionDetailsType,
  sectionType: SectionTypes,
  galleryMode: string, // Gallery section setting, if section type is gallery
  galleryMedia: GalleryMedia[],
  position: number,
  createdAt: string,
  updatedAt: string,
} & TimestampedModel & SortableFields & HasImageAndPhoto;

// Event types

export type Question = {
  event: number,
  text: string,
  question_type: string,
  required: boolean,
  choices?: string[],
} & TimestampedModel;

export type Answer = {
  rsvp: number,
  question: number,
  text: string,
}

// Named WebsiteEvent to avoid overlap with the existing js Event
export type WebsiteEvent = {
  site: number, // Site ID

  title: string,
  date: string,
  time: string,
  endDate: string,
  endTime: string,

  // Not a model field
  formattedAddress: string,
  description: RichTextType,
  rsvpEnabled: boolean,
  private: boolean,
  caption: string,
  questions: Question[],

  // Online Invitation fields
  hostedBy: string,
  openInvitation: boolean,
  // Guest list visibility is currently only an OI feature, but adding
  // it to site_details instead added considerable complexity
  showAttending: boolean,
  showNotAttending: boolean,
  showNotReplied: boolean,

  eventType: string
} & TimestampedModel & SortableFields & HasImageAndPhoto & AddressFields;

export type Guest = {
  contactEmail: string,
  addressbookContactId: string,
  firstName: string,
  lastName: string,
  maxParty: number,
  party: number,
} & TimestampedModel & SortableFields;

export type Invite = {
  event: number,
  guest: number,
  inviteSent: boolean,
} & TimestampedModel;

export type RSVP = {
  event: number,
  guest: Guest,
  source: string,
  attending: boolean | null,
  comments: string,
  answers: Answer[],
} & TimestampedModel;

// This is the response to selecting a name to RSVP
export type RSVPData = {
  contact: any,
  events: WebsiteEvent[],
  guests: Guest[],
}

// Wedding resource types

export type Attendant = {
  site: number, // Site ID

  title: string,
  name: string,
  bio: RichTextType, // maps to description
  photo: string | null,
  // Attendants uses 'photo' where everything else uses 'image' so we had to
  // name this one photoPk instead. This is just the 'photo' field on every
  // other model and will be removed when consolidated
  photoPk: number | null,
} & TimestampedModel & SortableFields;

export type Accommodation = {
  site: number, // Site ID

  title: string,
  url: string,
  description: RichTextType,
} & TimestampedModel & SortableFields & HasImageAndPhoto;

export type Registry = {
  site: number, // Site ID

  title: string,
  url: string,
  description: RichTextType,

  identifier: string,
  cashRegistryAccessToken: string,
  cashRegistryId: string,
} & TimestampedModel & SortableFields & HasImageAndPhoto;

// Comment types

export type Comment = {
  site: number, // Site ID

  authorName: string,
  authorEmail: string,
  authorVisitorId: string,
  comment: string,
  parentComment: number, // Parent comment ID
  emailAuthorOnReplies: boolean,
  isSubscribed: boolean
} & TimestampedModel;

// Image Types

export type ImageUpload = {
  uuid: string,
  height: number,
  width: number,
} & TimestampedModel;

// 3rd Party

export type IncomingCashRegistryCreationRequest = {
  public_token: string;
  slug: string;
  id: string;
  identifier: string;
  title: string;
  description: string;
  recipient: string;
  recipient_last_name: string;
  recipient_2_first_name: string;
  recipient_2_last_name: string;
  date: string;
  total_gifters: number;
};

export type IncomingCashRegistryUpdateRequest = {
  cashRegistryId: string;
  description: RichTextType;
  slug: string;
  title: string;
};

// Colorway and dekura theme typing

export type RawColorway = {
  code: string,
  color: string,
  external_id: string,
  id: string,
  title: string,
};

export type TypographySettings = {
  fontFamily: string,
  fontName: string,
  fontSizeFactor: number,
  fontStyle: string,
  fontVariant: string,
  fontWeight: number,
  letterSpacing: string,
  lineHeightFactor: number,
  textTransform: string,
}

export type DekuraHeaderTypographyGroup = {
  CoupleHorizontalNames?: TypographySettings,
  CoupleVerticalNames?: TypographySettings,
  CoupleVerticalNamesSeparator?: TypographySettings,
  EventDate?: TypographySettings,
  EventDateHeading?: TypographySettings,
  EventSmallHeadline?: TypographySettings,
  IntroMediumHeadline?: TypographySettings,
  IntroText?: TypographySettings,
  NavbarTop?: TypographySettings,
}

export type DekuraRequiredTypographyGroup = {
  Button?: TypographySettings,
  LargeHeadline?: TypographySettings,
  MediumHeadline?: TypographySettings,
  NavbarTop?: TypographySettings,
  Text?: TypographySettings,
  TinyHeadline?: TypographySettings,
}

export type DekuraTypographySettings = {
  required: DekuraRequiredTypographyGroup,
  header: DekuraHeaderTypographyGroup,
}

export type DekuraColorwayHeader = {
  backgroundColor: string,
  backgroundPatternPosition: string,
  backgroundPatternRepeat: string,
  fontColor: string,
  linkActiveColor: string,
  linkColor: string,
}

export type DekuraColorwayHeaderGroup = {
  InlineNavbar?: DekuraColorwayHeader,
  TopNavbar?: DekuraColorwayHeader,
  IntroMediumHeadline?: DekuraColorwayHeader,
  IntroContainer?: DekuraColorwayHeader,
}

export type DekuraArtwork = {
  LargeDivider: string,
  SmallDivider: string,
  SmallGraphic: string,
}

export type DekuraColors = {
  Button: string,
  LargeHeadline: string,
  MediumHeadline: string,
  SmallHeadline: string,
  TinyHeadline: string,
  backgroundColor: string,
  backgroundPatternPosition: string,
  backgroundPatternRepeat: string,
  fontColor: string,
  linkColor: string,
}

export type DekuraRequiredColorwayGroup = {
  artwork: DekuraArtwork,
  colors: DekuraColors,
}

export type DekuraColorway = {
  colorway: string, // Corresponds to colorwayCodeById
  header: DekuraColorwayHeaderGroup,
  required: DekuraRequiredColorwayGroup,
}

export type DekuraData = {
  colorways: DekuraColorway[],
  typography: DekuraTypographySettings
}

export type Theme = {
  attribution: 'string',
  colorwayCodeById: Record<number, string>,
  colorways: RawColorway[],
  dekuraData: DekuraData,
  fancyClothesData: any,
  title: string,
}

export enum ThemeColorCategory {
  background = 'background',
  text = 'text',
  secondaryBackground = 'secondaryBackground',
  secondaryText = 'secondaryText',
  linkAndButtonText = 'linkAndButtonText',
}

export type ThemeColors = {
  [ThemeColorCategory.background]: string;
  [ThemeColorCategory.text]: string;
  [ThemeColorCategory.secondaryBackground]: string;
  [ThemeColorCategory.secondaryText]: string;
  [ThemeColorCategory.linkAndButtonText]: string;
}

// Customer defined color theme
export type CustomColorTheme = {
  id: number;
  createdAt: string;
  updatedAt: string;
} & ThemeColors;
